import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import apiClient from '@/libs/apiClient'
import type { PushCampaign } from '@/types/marketing'

interface Pagination {
  currentPage: number
  lastPage: number
  perPage: number
  total: number
}

interface CampaignState {
  campaigns: PushCampaign[]
  pagination: Pagination | null
  loading: boolean
  error: string | null
}

const initialState: CampaignState = {
  campaigns: [],
  pagination: null,
  loading: false,
  error: null
}

// Fetch all campaigns
export const fetchCampaigns = createAsyncThunk<
  { campaigns: PushCampaign[]; pagination: Pagination },
  { limit?: number } | undefined,
  { rejectValue: string }
>('campaigns/fetchCampaigns', async (params = {}, { rejectWithValue }) => {
  try {
    const limit = params.limit || 1000
    const res = await apiClient.get('/admin/v1/campaigns', { params: { limit } })
    const json = res.data

    const pagination: Pagination = {
      currentPage: json.meta?.currentPage || 1,
      lastPage: json.meta?.lastPage || 1,
      perPage: json.meta?.perPage || 25,
      total: json.meta?.total || 0
    }

    return { campaigns: json.data as PushCampaign[], pagination }
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch campaigns')
  }
})

// Add Campaign
export const addCampaign = createAsyncThunk<PushCampaign, FormData, { rejectValue: string }>(
  'campaigns/addCampaign',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/admin/v1/campaigns/add', formData)
      const data = res.data

      return data.data as PushCampaign
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to add campaign')
    }
  }
)

// Update Campaign
export const updateCampaign = createAsyncThunk<PushCampaign, { id: string; formData: FormData }, { rejectValue: string }>(
  'campaigns/updateCampaign',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/admin/v1/campaigns/${id}/edit`, formData)
      const data = res.data

      return data.data as PushCampaign
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to update campaign')
    }
  }
)

// Delete Campaign
export const deleteCampaign = createAsyncThunk<string, string, { rejectValue: string }>(
  'campaigns/deleteCampaign',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/v1/campaigns/${id}/delete`)

      return id
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to delete campaign')
    }
  }
)

// Send Campaign Now
export const sendCampaign = createAsyncThunk<PushCampaign, string, { rejectValue: string }>(
  'campaigns/sendCampaign',
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/admin/v1/campaigns/${id}/send`)
      const data = res.data

      return data.data as PushCampaign
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to send campaign')
    }
  }
)

const campaignSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    clearCampaigns(state) {
      state.campaigns = []
      state.pagination = null
      state.error = null
    }
  },
  extraReducers: builder => {
    builder

      // Fetch
      .addCase(fetchCampaigns.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.loading = false
        state.campaigns = action.payload.campaigns
        state.pagination = action.payload.pagination
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch campaigns'
      })

      // Add
      .addCase(addCampaign.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(addCampaign.fulfilled, (state, action) => {
        state.loading = false
        state.campaigns.unshift(action.payload)
        if (state.pagination) state.pagination.total += 1
      })
      .addCase(addCampaign.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to add campaign'
      })

      // Update
      .addCase(updateCampaign.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        state.loading = false
        state.campaigns = state.campaigns.map(c => (c.id === action.payload.id ? action.payload : c))
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to update campaign'
      })

      // Delete
      .addCase(deleteCampaign.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteCampaign.fulfilled, (state, action) => {
        state.loading = false
        state.campaigns = state.campaigns.filter(c => c.id !== action.payload)
        if (state.pagination) state.pagination.total -= 1
      })
      .addCase(deleteCampaign.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to delete campaign'
      })

      // Send
      .addCase(sendCampaign.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(sendCampaign.fulfilled, (state, action) => {
        state.loading = false
        state.campaigns = state.campaigns.map(c => (c.id === action.payload.id ? action.payload : c))
      })
      .addCase(sendCampaign.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to send campaign'
      })
  }
})

export const { clearCampaigns } = campaignSlice.actions
export default campaignSlice.reducer
